import React from "react";
import styled from "styled-components";
import { MdLaunch } from "react-icons/md";
import { Flex, Text } from "rebass/styled-components";
import { useRecoilState, useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { useStatsigClient } from "@statsig/react-bindings";
import { isMobile } from "react-device-detect";

import { Button } from "MVVM/Components/Button";
import {
  ActiveOverlayPopUpState,
  KnownState,
  PlatformState,
} from "MVVM/State/iframe";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { IClip } from "MVVM/@types";
import { buildLink } from "MVVM/Utilities/Player";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { CloseButton } from "../../styledComponents";
import "./index.css";

export const SharePopUp = ({ clip }: { clip: IClip }) => {
  const platform = useRecoilValue(PlatformState);
  const { track } = useAnalyticsContext();
  const { t } = useTranslation("translation", {
    keyPrefix: "embeddablePlayer.sharePopUp",
  });
  const [activeOverlayPopUp, setActiveOverlayPopUp] = useRecoilState(
    ActiveOverlayPopUpState,
  );
  const { getExperiment } = useStatsigClient();
  const ajs_aid = window.rudderanalytics.getAnonymousId();
  const known = useRecoilValue(KnownState);
  const buttonLink = known
    ? buildLink({
        url: `https://allstar.gg/clip?clip=${clip.shareId}&utm_source=${
          clip.developer?.slug ||
          new URLSearchParams(window.location.search).get("platform") ||
          "unknown"
        }`,
      })
    : buildLink({
        url: `https://allstar.gg/register?utm_source=${
          clip.developer?.slug ||
          new URLSearchParams(window.location.search).get("platform") ||
          "unknown"
        }`,
        platform: platform,
        game: getMatchHistoryGameEnum(clip.game),
      });
  const title = (
    <>
      {t("title", "Share your clip on")} <span>Allstar.gg</span>!
    </>
  );
  const copy = t(
    "copy",
    "Create a FREE account on Allstar.gg to share your clips with your friends.",
  );
  const buttonText = t("buttonText", "Sign Up & Share");
  if (activeOverlayPopUp !== "signup_to_share") return null;
  return (
    <StyledShareContainer className="share-popup">
      <StyledCloseButton onClick={() => setActiveOverlayPopUp(null)} />
      <HeaderContainer className="share-popup-header">
        <StyledTitle className="share-popup-title" variant="text.titleXSmall">
          {title}
        </StyledTitle>
        <StyledSubtitle
          className="share-popup-subtitle"
          variant="text.paragraphMedium"
        >
          {copy}
        </StyledSubtitle>
      </HeaderContainer>
      <ButtonContainer className="share-popup-button-container">
        <StyledButton
          className="share-popup-button"
          size="small"
          flex={true}
          onClick={() => {
            track("Button - Click", {
              locationIdentifier: "share_popup_button",
              location: "embeddable_player_overlay",
            });
            const renderLinkToAppStore = getExperiment(
              "playlist_to_app_store",
            ).get("appone_redirect", false);
            if (renderLinkToAppStore && isMobile) {
              window.open(
                `https://allstar.gg/apponelink?utm_source=${
                  clip.developer?.slug ||
                  new URLSearchParams(window.location.search).get("platform") ||
                  "unknown"
                }&ajs_aid=${ajs_aid}`,
                "_blank",
              );
            } else {
              window.open(buttonLink, "_blank");
            }
          }}
        >
          {buttonText}
          <LaunchIcon />
        </StyledButton>
      </ButtonContainer>
    </StyledShareContainer>
  );
};

const StyledCloseButton = styled(CloseButton)`
  position: absolute;
  right: 10px;
  top: 10px;
`;
const ButtonContainer = styled(Flex)`
  align-items: center;
  flex-direction: row;
  gap: 8px;
  justify-content: space-around;
  width: 100%;
`;
const HeaderContainer = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
`;

const StyledSubtitle = styled(Text)`
  font-size: 8px;
  line-height: 12px;
`;
const StyledButton = styled(Button)`
  align-items: center;
  border-radius: 99px;
  display: flex;
  font-size: 10px;
  gap: 5px;
  height: 25px;
  justify-content: center;
  width: 100%;
`;

const LaunchIcon = styled(MdLaunch)`
  color: ${({ theme }) => theme.colors.ice};
  height: 16px;
  width: 16px;
`;

const StyledTitle = styled(Text)`
  font-size: 12px;
  line-height: 12px;

  span {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const StyledShareContainer = styled(Flex)`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.75);
  border-radius: 12px;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  margin: 0 auto;
  padding: 12px;
  pointer-events: auto;
  text-align: center;
  width: 240px;
`;
